import React, { useEffect, useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  fetchAgentConfig,
  updateAgentConfig,
} from "../../../redux/actions/agentConfigActions";
import { fetchOrganizations } from "../../../redux/actions/organizationActions";
import { format } from "date-fns";
import {
  AvatarLook,
  AvatarVoice,
  VoicePersonality,
  AvatarEnvironment,
  AvatarLocation,
  AvatarPose,
  AvatarShot,
  AvatarLanguage,
  VoiceEmotion,
  getAvatarLookDescription,
  getAvatarVoiceDescription,
  getVoicePersonalityDescription,
  getEnvironmentDescription,
  getAvatarLocationDescription,
  getAvatarPoseDescription,
  getAvatarShotDescription,
  getAvatarLanguageDescription,
  getVoiceEmotionDescription,
} from "../../../types/avatarConfig";
import { UpdateAgentConfigParams } from "../../../redux/actions/agentConfigActions";
import AssetPicker from "../assessments/AssetPicker";

const AgentConfigView: React.FC = () => {
  const { agentConfigId } = useParams<{ agentConfigId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { currentAgentConfig, loading, error } = useSelector(
    (state: RootState) => state.agentConfigs
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editedBotName, setEditedBotName] = useState("");
  const [editedBotDescription, setEditedBotDescription] = useState("");
  const [editedDisplayName, setEditedDisplayName] = useState("");
  const [editedDisplayNameTouched, setEditedDisplayNameTouched] = useState(false);
  const displayNameRef = useRef<HTMLDivElement>(null);
  const [
    editedOverridePersonalityGuidance,
    setEditedOverridePersonalityGuidance,
  ] = useState("");
  const [editedOrganizationId, setEditedOrganizationId] = useState("");
  const [editedAvatarLook, setEditedAvatarLook] = useState("");
  const [editedAvatarVoice, setEditedAvatarVoice] = useState("");
  const [editedVoicePersonality, setEditedVoicePersonality] = useState("");
  const [editedAvatarDefaultEmotion, setEditedAvatarDefaultEmotion] = useState("");
  const [editedAvatarLanguage, setEditedAvatarLanguage] = useState("");
  const [editedEnvironment, setEditedEnvironment] = useState("");
  const [editedAvatarLocation, setEditedAvatarLocation] = useState("");
  const [editedAvatarPose, setEditedAvatarPose] = useState("");
  const [editedAvatarShot, setEditedAvatarShot] = useState("");
  
  // Asset picker modal state
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [editedNamespaceAssetRegistry, setEditedNamespaceAssetRegistry] = useState<{
    namespace_assets: Record<string, string[]>;
  } | null>(null);
  
  // Temporary state for asset picker dialog
  const [tempNamespaceAssetRegistry, setTempNamespaceAssetRegistry] = useState<{
    namespace_assets: Record<string, string[]>;
  } | null>(null);

  const { organizations, loading: orgsLoading } = useSelector(
    (state: RootState) => state.organizations
  );

  useEffect(() => {
    if (agentConfigId) {
      dispatch(fetchAgentConfig(agentConfigId));
    }
    dispatch(fetchOrganizations());
  }, [dispatch, agentConfigId]);

  useEffect(() => {
    if (currentAgentConfig) {
      setEditedBotName(currentAgentConfig.bot_name);
      setEditedBotDescription(currentAgentConfig.bot_description);
      const displayName = currentAgentConfig.display_name || "";
      setEditedDisplayName(displayName);
      // Mark as touched if display_name is empty when loading
      setEditedDisplayNameTouched(displayName.trim() === "");
      setEditedOverridePersonalityGuidance(
        currentAgentConfig.override_personality_guidance || ""
      );
      setEditedOrganizationId(currentAgentConfig.organization_id || "");
      setEditedAvatarLook(currentAgentConfig.avatar_config?.avatar_look || "");
      setEditedAvatarVoice(
        currentAgentConfig.avatar_config?.avatar_voice || ""
      );
      setEditedVoicePersonality(
        currentAgentConfig.avatar_config?.avatar_voice_personality || ""
      );
      setEditedAvatarDefaultEmotion(
        currentAgentConfig.avatar_config?.avatar_default_emotion || ""
      );
      setEditedAvatarLanguage(
        currentAgentConfig.avatar_config?.avatar_language || ""
      );
      setEditedEnvironment(currentAgentConfig.avatar_config?.environment || "");
      setEditedAvatarLocation(
        currentAgentConfig.avatar_config?.avatar_location || ""
      );
      setEditedAvatarPose(currentAgentConfig.avatar_config?.avatar_pose || "");
      setEditedAvatarShot(currentAgentConfig.avatar_config?.shot || "");
      setEditedNamespaceAssetRegistry(currentAgentConfig.namespace_asset_registry || null);
    }
  }, [currentAgentConfig]);

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleEdit = () => {
    setIsEditing(true);
    // If display name is empty, scroll to it after a brief delay to allow rendering
    if (editedDisplayName.trim() === "") {
      setTimeout(() => {
        displayNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (currentAgentConfig) {
      setEditedBotName(currentAgentConfig.bot_name);
      setEditedBotDescription(currentAgentConfig.bot_description);
      const displayName = currentAgentConfig.display_name || "";
      setEditedDisplayName(displayName);
      setEditedDisplayNameTouched(displayName.trim() === "");
      setEditedOverridePersonalityGuidance(
        currentAgentConfig.override_personality_guidance || ""
      );
      setEditedOrganizationId(currentAgentConfig.organization_id || "");
      setEditedAvatarLook(currentAgentConfig.avatar_config?.avatar_look || "");
      setEditedAvatarVoice(
        currentAgentConfig.avatar_config?.avatar_voice || ""
      );
      setEditedVoicePersonality(
        currentAgentConfig.avatar_config?.avatar_voice_personality || ""
      );
      setEditedAvatarDefaultEmotion(
        currentAgentConfig.avatar_config?.avatar_default_emotion || ""
      );
      setEditedAvatarLanguage(
        currentAgentConfig.avatar_config?.avatar_language || ""
      );
      setEditedEnvironment(currentAgentConfig.avatar_config?.environment || "");
      setEditedAvatarLocation(
        currentAgentConfig.avatar_config?.avatar_location || ""
      );
      setEditedAvatarPose(currentAgentConfig.avatar_config?.avatar_pose || "");
      setEditedAvatarShot(currentAgentConfig.avatar_config?.shot || "");
      setEditedNamespaceAssetRegistry(currentAgentConfig.namespace_asset_registry || null);
    }
  };

  const handleSave = () => {
    if (!agentConfigId || !editedBotName || !editedBotDescription) {
      toast.error("Bot name and description are required");
      return;
    }
    
    if (editedDisplayName.trim() === "") {
      toast.error("Display name cannot be empty");
      return;
    }

    const updateData: UpdateAgentConfigParams = {
      id: agentConfigId,
      organization_id: editedOrganizationId || undefined,
      bot_name: editedBotName,
      bot_description: editedBotDescription,
      display_name: editedDisplayName || undefined,
      override_personality_guidance:
        editedOverridePersonalityGuidance || undefined,
      namespace_asset_registry: editedNamespaceAssetRegistry,
    };

    // Only include avatar_config if any avatar fields are set
    if (
      editedAvatarLook ||
      editedAvatarVoice ||
      editedVoicePersonality ||
      editedAvatarDefaultEmotion ||
      editedAvatarLanguage ||
      editedEnvironment ||
      editedAvatarLocation ||
      editedAvatarPose ||
      editedAvatarShot
    ) {
      updateData.avatar_config = {
        avatar_look: editedAvatarLook || undefined,
        avatar_voice: editedAvatarVoice || undefined,
        avatar_voice_personality: editedVoicePersonality || undefined,
        avatar_default_emotion: editedAvatarDefaultEmotion || undefined,
        avatar_language: editedAvatarLanguage || undefined,
        avatar_location: editedAvatarLocation || undefined,
        avatar_pose: editedAvatarPose || undefined,
        environment: editedEnvironment || undefined,
        shot: editedAvatarShot || undefined,
      };
    }

    dispatch(updateAgentConfig(updateData))
      .unwrap()
      .then(() => {
        toast.success("Agent Config updated successfully");
        setIsEditing(false);
      })
      .catch((error: any) => {
        toast.error(`Failed to update agent config: ${error.message}`);
      });
  };

  if (loading && !currentAgentConfig) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!currentAgentConfig) {
    return <div>Agent Config not found</div>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Typography variant="h5" gutterBottom>
        Agent Config Details
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <TextField
          label="Config ID"
          value={currentAgentConfig.id}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel id="organization-select-label">Organization</InputLabel>
          <Select
            labelId="organization-select-label"
            value={
              isEditing
                ? editedOrganizationId
                : currentAgentConfig.organization_id || ""
            }
            onChange={(e) => setEditedOrganizationId(e.target.value)}
            label="Organization"
            disabled={!isEditing}
            sx={
              !isEditing
                ? {
                    backgroundColor: "#f5f5f5",
                    opacity: 0.7,
                  }
                : {}
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {orgsLoading ? (
              <MenuItem disabled>Loading organizations...</MenuItem>
            ) : (
              organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <TextField
          label="Bot Name"
          value={isEditing ? editedBotName : currentAgentConfig.bot_name}
          onChange={(e) => setEditedBotName(e.target.value)}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          ref={displayNameRef}
          label="Display Name"
          value={isEditing ? editedDisplayName : currentAgentConfig.display_name || ""}
          onChange={(e) => setEditedDisplayName(e.target.value)}
          onBlur={() => setEditedDisplayNameTouched(true)}
          error={isEditing && editedDisplayNameTouched && editedDisplayName.trim() === ""}
          helperText={
            isEditing && editedDisplayNameTouched && editedDisplayName.trim() === ""
              ? "Display Name cannot be empty. A user-friendly display name (e.g., 'Sales Training Bot')"
              : "A user-friendly display name (e.g., 'Sales Training Bot')"
          }
          fullWidth
          margin="normal"
          placeholder="e.g., Sales Training Bot"
          required
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Bot Description"
          value={
            isEditing
              ? editedBotDescription
              : currentAgentConfig.bot_description
          }
          onChange={(e) => setEditedBotDescription(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={4}
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Override Personality Guidance"
          value={
            isEditing
              ? editedOverridePersonalityGuidance
              : currentAgentConfig.override_personality_guidance || ""
          }
          onChange={(e) => setEditedOverridePersonalityGuidance(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={6}
          placeholder="Leave empty to use default personality guidance"
          InputProps={{
            readOnly: !isEditing,
            sx: !isEditing
              ? {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                }
              : {},
          }}
          InputLabelProps={{ shrink: true }}
        />

        {/* Avatar Configuration Accordion */}
        <Accordion sx={{ mt: 2, mb: 2 }} defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="avatar-config-content"
            id="avatar-config-header"
          >
            <Typography>Avatar Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="avatar-look-label">Avatar Look</InputLabel>
                  <Select
                    labelId="avatar-look-label"
                    value={
                      isEditing
                        ? editedAvatarLook
                        : currentAgentConfig.avatar_config?.avatar_look || ""
                    }
                    onChange={(e) => setEditedAvatarLook(e.target.value)}
                    label="Avatar Look"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarLook).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getAvatarLookDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the avatar's visual appearance
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="avatar-voice-label">Avatar Voice</InputLabel>
                  <Select
                    labelId="avatar-voice-label"
                    value={
                      isEditing
                        ? editedAvatarVoice
                        : currentAgentConfig.avatar_config?.avatar_voice || ""
                    }
                    onChange={(e) => setEditedAvatarVoice(e.target.value)}
                    label="Avatar Voice"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarVoice).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getAvatarVoiceDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the avatar's voice settings
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="voice-personality-label">Voice Personality</InputLabel>
                  <Select
                    labelId="voice-personality-label"
                    value={
                      isEditing
                        ? editedVoicePersonality
                        : currentAgentConfig.avatar_config?.avatar_voice_personality || ""
                    }
                    onChange={(e) => setEditedVoicePersonality(e.target.value)}
                    label="Voice Personality"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(VoicePersonality).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getVoicePersonalityDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the avatar's voice personality filter
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="default-emotion-label">Default Emotion</InputLabel>
                  <Select
                    labelId="default-emotion-label"
                    value={
                      isEditing
                        ? editedAvatarDefaultEmotion
                        : currentAgentConfig.avatar_config?.avatar_default_emotion || ""
                    }
                    onChange={(e) => setEditedAvatarDefaultEmotion(e.target.value)}
                    label="Default Emotion"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(VoiceEmotion).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getVoiceEmotionDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Default emotion when nothing is coming from the model
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="avatar-language-label">Language</InputLabel>
                  <Select
                    labelId="avatar-language-label"
                    value={
                      isEditing
                        ? editedAvatarLanguage
                        : currentAgentConfig.avatar_config?.avatar_language || ""
                    }
                    onChange={(e) => setEditedAvatarLanguage(e.target.value)}
                    label="Language"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarLanguage).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getAvatarLanguageDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Preferred language for the avatar to speak
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="environment-label">Environment</InputLabel>
                  <Select
                    labelId="environment-label"
                    value={
                      isEditing
                        ? editedEnvironment
                        : currentAgentConfig.avatar_config?.environment || ""
                    }
                    onChange={(e) => setEditedEnvironment(e.target.value)}
                    label="Environment"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarEnvironment).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getEnvironmentDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the environment setting for the avatar
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="avatar-location-label">
                    Avatar Location
                  </InputLabel>
                  <Select
                    labelId="avatar-location-label"
                    value={
                      isEditing
                        ? editedAvatarLocation
                        : currentAgentConfig.avatar_config?.avatar_location ||
                          ""
                    }
                    onChange={(e) => setEditedAvatarLocation(e.target.value)}
                    label="Avatar Location"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarLocation).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getAvatarLocationDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the avatar's location in the scene
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="avatar-pose-label">Avatar Pose</InputLabel>
                  <Select
                    labelId="avatar-pose-label"
                    value={
                      isEditing
                        ? editedAvatarPose
                        : currentAgentConfig.avatar_config?.avatar_pose || ""
                    }
                    onChange={(e) => setEditedAvatarPose(e.target.value)}
                    label="Avatar Pose"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarPose).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getAvatarPoseDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select the avatar's pose</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="avatar-shot-label">Camera Shot</InputLabel>
                  <Select
                    labelId="avatar-shot-label"
                    value={
                      isEditing
                        ? editedAvatarShot
                        : currentAgentConfig.avatar_config?.shot || ""
                    }
                    onChange={(e) => setEditedAvatarShot(e.target.value)}
                    label="Camera Shot"
                    disabled={!isEditing}
                    sx={
                      !isEditing
                        ? {
                            backgroundColor: "#f5f5f5",
                            opacity: 0.7,
                          }
                        : {}
                    }
                  >
                    <MenuItem value="">
                      <em>Default</em>
                    </MenuItem>
                    {Object.entries(AvatarShot).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {key} - {getAvatarShotDescription(value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Select the camera shot framing
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Linked Assets Section */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Linked Assets
            </Typography>
            {isEditing && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  // Initialize temp state with current values
                  setTempNamespaceAssetRegistry(editedNamespaceAssetRegistry);
                  setAssetPickerOpen(true);
                }}
                size="small"
              >
                Edit Assets
              </Button>
            )}
          </Box>
          {Object.entries((editedNamespaceAssetRegistry?.namespace_assets || currentAgentConfig.namespace_asset_registry?.namespace_assets || {})).length > 0 ? (
            Object.entries((editedNamespaceAssetRegistry?.namespace_assets || currentAgentConfig.namespace_asset_registry?.namespace_assets || {})).map(
              ([namespace, assetIds]) => (
                <Box key={namespace} sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {namespace}
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {assetIds && assetIds.length > 0 ? (
                      assetIds.map((assetId, index) => (
                        <Typography key={index} variant="body2" sx={{ display: 'block' }}>
                          {assetId}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        No assets configured
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            )
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No assets configured
            </Typography>
          )}
        </Box>

        <TextField
          label="Created At"
          value={
            currentAgentConfig.created_at
              ? format(
                  new Date(currentAgentConfig.created_at),
                  "yyyy-MM-dd HH:mm:ss"
                )
              : ""
          }
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Updated At"
          value={
            currentAgentConfig.updated_at
              ? format(
                  new Date(currentAgentConfig.updated_at),
                  "yyyy-MM-dd HH:mm:ss"
                )
              : "Never"
          }
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Box display="flex" alignItems="center" marginTop={3} gap={2}>
          {!isEditing ? (
            <>
              <Button variant="contained" color="primary" onClick={handleEdit}>
                Edit
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate("/dashboard/agent-configs")}
              >
                Back
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={editedDisplayName.trim() === ""}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Asset Picker Dialog */}
      <Dialog
        open={assetPickerOpen}
        onClose={() => {
          setAssetPickerOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Add Assets to Agent Config</Typography>
            <IconButton
              onClick={() => {
                setAssetPickerOpen(false);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <AssetPicker
            namespace={editedOrganizationId ? `${editedOrganizationId}:assessments` : ""}
            selectedNamespaceAssetRegistry={tempNamespaceAssetRegistry}
            onSelectionChange={setTempNamespaceAssetRegistry}
            showToggle={false}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssetPickerOpen(false);
              setTempNamespaceAssetRegistry(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              // Apply the temporary changes to the edited state
              setEditedNamespaceAssetRegistry(tempNamespaceAssetRegistry);
              setAssetPickerOpen(false);
              setTempNamespaceAssetRegistry(null);
            }}
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentConfigView;
